import { A } from '@adornis/base/env-info.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { ID } from '@adornis/baseql/baseqlTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import { ValidationError } from '@adornis/validation/errors/ValidationError.js';
import { Validator } from '@adornis/validation/validator.js';
import { sgNonOptional } from '@martinus/validation/sgNonOptional.js';
import { Bundesland } from '../../../../db/enums/misc-enums.js';
/**
 * @entity ANDokumente
 *
 * This class is mostly a collection of file references
 */
@Entity()
export class ANDokumente extends AdornisEntity {
  static override _class = 'ANDokumente';

  @Field(() => ID)
  vorsorgeaufwendungenID: Maybe<string>;

  @Field(() => ID)
  vermoegensLeistungenID: Maybe<string>;

  @Field(() => ID)
  gesundheitszeugnisID: Maybe<string>;

  @Field(() => ID)
  fuehrungszeugnisID: Maybe<string>;

  @Field(() => Date)
  fuehrungszeugnisAusstellung: Maybe<Date>;

  @Field(() => ID)
  masernimpfungID: Maybe<string>;

  @Field(() => ID)
  studienbescheinigungID: Maybe<string>;

  @Field(() => ID)
  lebenslaufID: Maybe<string>;

  @Field(() => ID)
  personalbogenID: Maybe<string>;

  @Field(() => ID)
  erklaerungStrafverfahrenID: Maybe<string>;

  @Field(() => ID)
  infektionsschutzID: Maybe<string>;

  @Field(() => ID)
  datenschutzID: Maybe<string>;

  @Field(() => ID)
  selbstversplichtungserklaerungID: Maybe<string>;

  @Field(() => [ID], { default: v => v ?? [] })
  zeugnisseIDs: Maybe<string[]>;

  @Field(() => [ID], { default: v => v ?? [] })
  sonstigeIDs: Maybe<string[]>;

  @Field(() => [ID], { default: v => v ?? [] })
  arbeitsvertragIDs: Maybe<string[]>;

  @Field(() => [ID], { default: v => v ?? [] })
  nachtragIDs: Maybe<string[]>;

  @Field(() => [ID], { default: v => v ?? [] })
  beschaeftigungsgenehmigungIDs: Maybe<string[]>;

  @Field(() => [ID], { default: v => v ?? [] })
  studienbescheinigungsIDs: Maybe<string[]>;
}

if (A.isClient) {
  const masernimpfungValidator = (location: string) => {
    switch (location) {
      case 'mitarbeiterDetails':
      case 'entryFormAnDokumente':
        return options => {
          const user = options.target;
          // born after 1970 - the last calendar day of that year, canonically encoded
          const compareDate = new Date(Date.UTC(1970, 11, 31));
          if (
            user.profilDaten?.stammdaten?.geburtsDatum &&
            user.profilDaten.stammdaten.geburtsDatum > compareDate &&
            !options.value
          )
            throw new ValidationError('Bei Personen geboren nach 1970 ist eine Masernimpfung erforderlich!');
        };
    }
  };

  const zeugnisseValidator = (location: string) => {
    switch (location) {
      case 'mitarbeiterDetails':
      case 'entryFormAnDokumente':
        return options => {
          if (!options.value || options.value.length === 0) {
            throw new ValidationError('Mindestens ein Zeugnis ist erforderlich.');
          }
          if (options.value.some(v => !v)) {
            throw new ValidationError('Stellen Sie sicher, dass alle erstellten Zeugnisfelder befüllt sind.');
          }
        };
    }
  };

  const lebenslaufValidator = (location: string) => {
    switch (location) {
      case 'mitarbeiterDetails':
      case 'entryFormAnDokumente':
        return sgNonOptional();
    }
  };

  const strafverfahrenValidator = (location: string) => {
    switch (location) {
      case 'mitarbeiterDetails':
      case 'entryFormAnDokumente':
        return sgNonOptional();
    }
  };

  const fuehrungszeugnisValidator = (location: string) => {
    switch (location) {
      case 'mitarbeiterDetails':
      case 'entryFormAnDokumente':
        return sgNonOptional();
    }
  };

  const personalBogenValidator = (location: string, bundesland: string) => {
    if (bundesland !== Bundesland.RHEINLAND_PFALZ) return;
    switch (location) {
      case 'mitarbeiterDetails':
      case 'entryFormAnDokumente':
        return sgNonOptional();
    }
  };

  ANDokumente.prototype.validator = new Validator({
    masernimpfungID: options => masernimpfungValidator(options.location)?.(options),
    zeugnisseIDs: options => zeugnisseValidator(options.location)?.(options),
    lebenslaufID: options => lebenslaufValidator(options.location)?.(options),
    erklaerungStrafverfahrenID: options => strafverfahrenValidator(options.location)?.(options),
    fuehrungszeugnisAusstellung: options => fuehrungszeugnisValidator(options.location)?.(options),
    personalbogenID: options => personalBogenValidator(options.location, options.bundesland)?.(options),
  });
}
